import { Typography } from '@mui/material';

import { Divider } from '../Divider/Divider';
import { useColors } from '../ThemeProvider/useColors';

type HeaderProps = {
  headerLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
  color?: 'black' | 'white';
  underline?: boolean;
  children: React.ReactNode;
};

function HeaderBase({
  headerLevel,
  fontSize,
  color,
  underline,
  children,
}: HeaderProps & { fontSize: string }) {
  const { secondary, black, white } = useColors();

  const resolvedColor = color === 'white' ? white : black;

  return (
    <Typography
      variant={headerLevel}
      fontSize={fontSize}
      fontWeight="bold"
      maxWidth="75ch"
      sx={{
        color: resolvedColor,
        wordBreak: 'break-word',
        wordWrap: 'break-word',
      }}
    >
      {children}

      {underline && <Divider width="5rem" height="0.125rem" color={secondary} />}
    </Typography>
  );
}

export function Header1({
  headerLevel = 'h1',
  color = 'black',
  underline = false,
  children,
}: HeaderProps) {
  return (
    <HeaderBase
      headerLevel={headerLevel}
      fontSize="1.5rem"
      color={color}
      underline={underline}
      children={children}
    />
  );
}

export function Header2({
  headerLevel = 'h2',
  color = 'black',
  underline = false,
  children,
}: HeaderProps) {
  return (
    <HeaderBase
      headerLevel={headerLevel}
      fontSize="1.4rem"
      color={color}
      underline={underline}
      children={children}
    />
  );
}

export function Header3({
  headerLevel = 'h3',
  color = 'black',
  underline = false,
  children,
}: HeaderProps) {
  return (
    <HeaderBase
      headerLevel={headerLevel}
      fontSize="1.3rem"
      color={color}
      underline={underline}
      children={children}
    />
  );
}

export function Header4({
  headerLevel = 'h4',
  color = 'black',
  underline = false,
  children,
}: HeaderProps) {
  return (
    <HeaderBase
      headerLevel={headerLevel}
      fontSize="1.2rem"
      color={color}
      underline={underline}
      children={children}
    />
  );
}

export function Header5({
  headerLevel = 'h5',
  color = 'black',
  underline = false,
  children,
}: HeaderProps) {
  return (
    <HeaderBase
      headerLevel={headerLevel}
      fontSize="1.1rem"
      color={color}
      underline={underline}
      children={children}
    />
  );
}
